// Every command the aligner has, in the order the palette lists them and each menu section orders
// them, in the registry Interplae's shell uses (src/core/registry.ts). A command's chord comes
// from src/core/chords.ts by its id; nothing here names a key. The keymap, the menu bar, the
// palette, the toolbar and the shortcuts sheet all read this one table, as the shell's do its
// own (src/app/commands.ts).

import type { PaletteListed, PaletteMeta } from '../../core/palette.ts'
import { Registry } from '../../core/registry.ts'
import { CHORDS } from '../core/chords.ts'
import { at, type AlignMenus } from '../core/menu.ts'
import type { Column, Direction } from '../core/types.ts'
import type { Shell } from './shell.ts'

/// The open editor as a command sees it, set afresh on every render.
export interface Aligner {
  /// No operation is under way.
  idle: boolean
  /// The cursor is on a row.
  has: boolean
  /// The cursor's cell has text.
  text: boolean
  /// A run of more than one cell is selected.
  several: boolean
  /// The first cell of the selection, or the cursor's, has text.
  topText: boolean
  edited: boolean
  locked: boolean
  excluded: boolean
  canUndo: boolean
  canRedo: boolean
  equalColumns: boolean
  /// Where saves and exports go: "your downloads" or the folder.
  where: string
  open(): void
  save(): void
  exportTmx(): void
  undo(): void
  redo(): void
  edit(): void
  revert(): void
  split(): void
  join(): void
  move(direction: Direction): void
  lockRealign(): void
  lock(): void
  exclude(): void
  pinpoint(): void
  realign(): void
  nextFlagged(): void
  /// Review with Claude (brief 48).
  review(): void
  /// The person may send the TMX to an organisation's memory: a Manager or an Admin (brief 49).
  memory: boolean
  sendToMemory(): void
  font(column: Column, step: number): void
  equal(): void
  say(text: string): void
}

const idle = (s: Shell) => s.a.idle
const row = (s: Shell) => s.a.idle && s.a.has
const text = (s: Shell) => s.a.idle && s.a.text
/// For the commands that act on one cell only: none of them runs on a selected run.
const one = (s: Shell) => s.a.idle && !s.a.several
const oneText = (s: Shell) => text(s) && !s.a.several
const oneRow = (s: Shell) => row(s) && !s.a.several

/// The registry, for menus the page draws or, in the desktop app, the system's (brief 46).
export function commands(menus: 'page' | 'system' = 'page'): Registry<Shell, AlignMenus> {
  const r = new Registry<Shell, AlignMenus>(menus, CHORDS)

  r.add({ id: 'session.open', title: 'Open other documents', menu: at('file', 'open', { asks: true }), run: s => s.a.open() })
  r.add({ id: 'session.save', title: 'Save the session file', menu: at('file', 'save', { label: 'Save session' }), when: idle, run: s => s.a.save() })
  r.add({ id: 'session.export', title: 'Export TMX', help: 'Writes every two-sided row that is not excluded and says how many rows it left out.', menu: at('file', 'export'), when: idle, run: s => s.a.exportTmx() })
  r.add({ id: 'session.sendToMemory', title: 'Send to memory', help: 'Imports the TMX Export TMX writes into the organisation’s memory as a new source.', menu: at('file', 'export', { asks: true, present: s => s.a.memory }), when: s => s.a.idle && s.a.memory, run: s => s.a.sendToMemory() })

  r.add({ id: 'edit.undo', title: 'Undo', menu: at('edit', 'history'), when: s => s.a.idle && s.a.canUndo, run: s => s.a.undo() })
  r.add({ id: 'edit.redo', title: 'Redo', menu: at('edit', 'history'), when: s => s.a.idle && s.a.canRedo, run: s => s.a.redo() })
  r.add({ id: 'cell.edit', title: 'Edit the cell’s text', help: 'Return confirms and Escape cancels. The row is flagged as edited.', menu: at('edit', 'text', { label: 'Edit text' }), when: oneText, run: s => s.a.edit() })
  r.add({ id: 'cell.revert', title: 'Go back to the document’s text', menu: at('edit', 'text'), when: s => one(s) && s.a.edited, run: s => s.a.revert() })

  r.add({ id: 'row.split', title: 'Split at the caret', help: 'Return puts a caret in the cell first.', menu: at('row', 'structure'), when: oneText, run: s => s.a.split() })
  r.add({ id: 'row.join', title: 'Join with the cell below', help: 'With several cells selected, joins them into one.', menu: at('row', 'structure'), when: row, run: s => s.a.join() })
  r.add({ id: 'row.moveUp', title: 'Move the cell up', help: 'Moves the cell, or every selected cell, up a row. A blank cell above is filled; one with text takes the cell on its end. The cursor goes with it.', menu: at('row', 'move'), when: s => text(s) || (row(s) && s.a.several), run: s => s.a.move('Up') })
  r.add({ id: 'row.moveDown', title: 'Move the cell down', help: 'Moves the cell, or every selected cell, down a row. A blank cell below is filled; one with text takes the cell on its start. The cursor goes with it.', menu: at('row', 'move'), when: s => text(s) || (row(s) && s.a.several), run: s => s.a.move('Down') })
  r.add({ id: 'row.lockRealign', title: 'Lock and realign around it', help: 'Keeps the row as it is and aligns the unlocked stretches on either side again.', menu: at('row', 'lock'), when: oneRow, run: s => s.a.lockRealign() })
  r.add({ id: 'row.lock', title: 'Lock without realigning', menu: at('row', 'lock', { checked: s => s.a.locked }), when: oneRow, run: s => s.a.lock() })
  r.add({ id: 'row.pinpoint', title: 'Pinpoint Align', help: 'Pick a cell, or select several, then the cell in the other column that belongs beside the first; they become locked rows.', menu: at('row', 'lock', { asks: true }), when: s => s.a.idle && s.a.topText, run: s => s.a.pinpoint() })
  r.add({ id: 'row.exclude', title: 'Exclude from the export', menu: at('row', 'lock', { checked: s => s.a.excluded }), when: oneRow, run: s => s.a.exclude() })
  r.add({ id: 'row.realign', title: 'Realign every unlocked stretch', menu: at('row', 'lock'), when: idle, run: s => s.a.realign() })
  r.add({ id: 'row.nextFlagged', title: 'Go to the next flagged row', menu: at('row', 'go', { label: 'Next flagged row' }), when: idle, run: s => s.a.nextFlagged() })
  r.add({ id: 'row.review', title: 'Review with Claude', help: 'Sends the unlocked rows to Claude, which corrects which translation goes with which sentence. It shows what goes and the estimated cost first.', menu: at('row', 'review', { asks: true }), when: idle, run: s => s.a.review() })

  r.add({ id: 'view.sourceBigger', title: 'Larger source text', menu: at('view', 'source'), run: s => s.a.font('Source', 1) })
  r.add({ id: 'view.sourceSmaller', title: 'Smaller source text', menu: at('view', 'source'), run: s => s.a.font('Source', -1) })
  r.add({ id: 'view.targetBigger', title: 'Larger target text', menu: at('view', 'target'), run: s => s.a.font('Target', 1) })
  r.add({ id: 'view.targetSmaller', title: 'Smaller target text', menu: at('view', 'target'), run: s => s.a.font('Target', -1) })
  r.add({ id: 'view.equalColumns', title: 'Columns of equal width', menu: at('view', 'columns'), when: s => !s.a.equalColumns, run: s => s.a.equal() })

  r.add({ id: 'palette.open', title: 'Command palette', menu: at('help', 'find', { asks: true }), run: s => s.togglePalette() })
  r.add({ id: 'shortcuts.open', title: 'Show the keyboard shortcuts', menu: at('help', 'find', { label: 'Keyboard shortcuts' }), run: s => s.set({ shortcuts: !s.state.shortcuts, menu: null, palette: false }) })

  describePalette(r)
  return r
}

const HIDDEN: PaletteMeta<Shell> = { hidden: true }
const listed = (label: string, rest: Omit<PaletteListed<Shell>, 'label'> = {}): PaletteListed<Shell> => ({ label, ...rest })
/// Its place in the empty palette's group.
const suggest = (order: number) => () => order

/// How the command palette lists each command: a short label, the other words people search for,
/// and the empty palette's group, which holds the toolbar's actions in the toolbar's order.
function describePalette(r: Registry<Shell, AlignMenus>): void {
  const table: Record<string, PaletteMeta<Shell>> = {
    'palette.open': HIDDEN,
    'session.open': listed('Open other documents…', { aliases: ['new alignment', 'close', 'start again'] }),
    'session.save': listed('Save session', { aliases: ['download session', 'keep'], note: s => `To ${s.a.where}` }),
    'session.export': listed('Export TMX', { aliases: ['download tmx', 'translation memory', 'save tmx'], note: s => `To ${s.a.where}`, suggest: suggest(9) }),
    'session.sendToMemory': listed('Send to memory…', { aliases: ['import into memory', 'organisation memory', 'translation memory', 'memories'] }),
    'edit.undo': listed('Undo', { aliases: ['take back'] }),
    'edit.redo': listed('Redo', { aliases: ['do again'] }),
    'cell.edit': listed('Edit text', { aliases: ['change text', 'correct', 'type'] }),
    'cell.revert': listed('Go back to the document’s text', { aliases: ['revert', 'undo edit', 'original text'] }),
    'row.split': listed('Split at the caret', { aliases: ['divide', 'cut row', 'break'], suggest: suggest(1) }),
    'row.join': listed('Join with the cell below', { aliases: ['merge', 'combine', 'join selected'], note: s => (s.a.several ? 'Joins the selected cells' : undefined), suggest: suggest(2) }),
    'row.moveUp': listed('Move the cell up', { aliases: ['merge up', 'push up', 'shift up'], note: s => (s.a.several ? 'Moves the selected cells' : undefined), suggest: suggest(3) }),
    'row.moveDown': listed('Move the cell down', { aliases: ['merge down', 'push down', 'shift down'], note: s => (s.a.several ? 'Moves the selected cells' : undefined), suggest: suggest(4) }),
    'row.lockRealign': listed('Lock and realign', { aliases: ['fix row', 'anchor', 'approve'], suggest: suggest(5) }),
    'row.lock': listed('Lock without realigning', { aliases: ['unlock', 'keep row'], note: s => (s.a.locked ? 'Unlocks the row' : undefined) }),
    'row.pinpoint': listed('Pinpoint Align…', { aliases: ['pair cells', 'match cells', 'anchor'], suggest: suggest(6) }),
    'row.exclude': listed('Exclude from the export', { aliases: ['skip row', 'leave out', 'include'], note: s => (s.a.excluded ? 'Includes the row again' : undefined), suggest: suggest(7) }),
    'row.realign': listed('Realign every unlocked stretch', { aliases: ['align again', 'redo alignment'] }),
    'row.nextFlagged': listed('Next flagged row', { aliases: ['problem', 'warning', 'review'], suggest: suggest(8) }),
    'row.review': listed('Review with Claude…', { aliases: ['claude', 'language model', 'check the alignment', 'correct the pairs', 'ai'] }),
    'view.sourceBigger': listed('Larger source text', { aliases: ['font size', 'zoom in'] }),
    'view.sourceSmaller': listed('Smaller source text', { aliases: ['font size', 'zoom out'] }),
    'view.targetBigger': listed('Larger target text', { aliases: ['font size', 'zoom in'] }),
    'view.targetSmaller': listed('Smaller target text', { aliases: ['font size', 'zoom out'] }),
    'view.equalColumns': listed('Columns of equal width', { aliases: ['reset divider', 'column width'] }),
    'shortcuts.open': listed('Keyboard shortcuts', { aliases: ['keys', 'help', 'chords'] }),
  }
  for (const c of r.all()) {
    const meta = table[c.id]
    if (!meta) throw new Error('no palette line for ' + c.id)
    r.describe(c.id, meta)
  }
}

/// The toolbar's buttons, in groups: the most-used actions (the aligner's handoff, "Next").
export const TOOLBAR: readonly { id: string; label: string }[][] = [
  [{ id: 'row.split', label: 'Split' }, { id: 'row.join', label: 'Join' }],
  [{ id: 'row.moveUp', label: 'Move ↑' }, { id: 'row.moveDown', label: 'Move ↓' }],
  [{ id: 'row.lockRealign', label: 'Lock & realign' }, { id: 'row.pinpoint', label: 'Pinpoint' }, { id: 'row.exclude', label: 'Exclude' }],
  [{ id: 'row.nextFlagged', label: 'Next flagged' }],
]
